import React, { useState, useMemo } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import { EntityAssignmentSelector } from './EntityAssignmentSelector';
import { EntityAssignment, EntityType } from '../../types/workflow';
import { Farmer } from '../../types/farmers';
import { Agent } from '../../types/agent';
import { Lab } from '../../types/lab';
import FarmerDialog from '../Farmers/FarmerDialog';
import AgentDialog from '../LookupTables/Agents/AgentDialog';
import LabDialog from '../LookupTables/Labs/LabDialog';

interface CreatableEntityAssignmentSelectorProps {
  value?: EntityAssignment | null;
  onChange: (value: EntityAssignment | null) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  allowedEntityTypes?: EntityType[];
  allowCreate?: boolean;
}

/**
 * CreatableEntityAssignmentSelector
 *
 * Wraps EntityAssignmentSelector and adds an "Add" button that opens a menu
 * for creating new Farmer, Agent, or Lab entities inline. When an entity is
 * created, it is automatically selected as the current assignment.
 */
export const CreatableEntityAssignmentSelector: React.FC<CreatableEntityAssignmentSelectorProps> = ({
  value,
  onChange,
  label,
  required,
  disabled,
  error,
  helperText,
  allowedEntityTypes,
  allowCreate = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openDialog, setOpenDialog] = useState<'farmer' | 'agent' | 'lab' | null>(null);

  const menuOpen = Boolean(anchorEl);

  const entityTypesToSearch = useMemo(() => {
    return allowedEntityTypes || (['Farmer', 'Agent', 'Lab'] as EntityType[]);
  }, [allowedEntityTypes]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (dialogType: 'farmer' | 'agent' | 'lab') => {
    handleMenuClose();
    setOpenDialog(dialogType);
  };

  const handleFarmerCreated = (farmer: Farmer) => {
    const assignment: EntityAssignment = {
      entityType: 'Farmer',
      entityId: farmer.idFarmer,
      entityName: farmer.fullName || `${farmer.firstName} ${farmer.surname || ''}`.trim(),
      email: farmer.emailAddress,
      isSageReady: farmer.sageDetails?.isSageReady ?? false,
    };
    onChange(assignment);
  };

  const handleAgentCreated = (agent: Agent) => {
    const assignment: EntityAssignment = {
      entityType: 'Agent',
      entityId: agent.idAgent,
      entityName: agent.agentName,
      email: agent.emailAddress,
      isSageReady: agent.sageDetails?.isSageReady ?? false,
      // A just-created agent has no Sage Sales Rep link yet.
      hasSageSalesRepLink: false,
    };
    onChange(assignment);
  };

  const handleLabCreated = (lab: Lab) => {
    const assignment: EntityAssignment = {
      entityType: 'Lab',
      entityId: lab.idLab,
      entityName: lab.labName,
      email: lab.emailAddress,
      isSageReady: lab.sageDetails?.isSageReady ?? false,
    };
    onChange(assignment);
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <Box sx={{ flex: 1 }}>
          <EntityAssignmentSelector
            value={value}
            onChange={onChange}
            label={label}
            required={required}
            disabled={disabled}
            error={error}
            helperText={helperText}
            allowedEntityTypes={allowedEntityTypes}
          />
        </Box>
        {allowCreate && (
          <IconButton
            onClick={handleMenuOpen}
            disabled={disabled}
            size="small"
            aria-label="Create new entity"
            sx={{ mt: 1 }}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
      >
        {entityTypesToSearch.includes('Farmer') && (
          <MenuItem onClick={() => handleMenuItemClick('farmer')}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>New Farmer</ListItemText>
          </MenuItem>
        )}
        {entityTypesToSearch.includes('Agent') && (
          <MenuItem onClick={() => handleMenuItemClick('agent')}>
            <ListItemIcon>
              <BusinessIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>New Agent</ListItemText>
          </MenuItem>
        )}
        {entityTypesToSearch.includes('Lab') && (
          <MenuItem onClick={() => handleMenuItemClick('lab')}>
            <ListItemIcon>
              <ScienceIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>New Lab</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Entity creation dialogs - only rendered when allowCreate is true and entity type is allowed */}
      {allowCreate && (
        <>
          {entityTypesToSearch.includes('Farmer') && (
            <FarmerDialog
              open={openDialog === 'farmer'}
              farmer={null}
              onClose={() => setOpenDialog(null)}
              onCreated={handleFarmerCreated}
            />
          )}
          {entityTypesToSearch.includes('Agent') && (
            <AgentDialog
              open={openDialog === 'agent'}
              agent={null}
              onClose={() => setOpenDialog(null)}
              onCreated={handleAgentCreated}
            />
          )}
          {entityTypesToSearch.includes('Lab') && (
            <LabDialog
              open={openDialog === 'lab'}
              lab={null}
              onClose={() => setOpenDialog(null)}
              onCreated={handleLabCreated}
            />
          )}
        </>
      )}
    </>
  );
};

export default CreatableEntityAssignmentSelector;
